import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../shared/firebase/client';
import { fetchFxRates, fetchQuotes } from './pricesRepo';

/** ตรงกับ `RefreshPricesResult` ฝั่ง function */
export interface RefreshPricesResult {
  refreshed: number;
  cached: number;
  failed: { priceKey: string; reason: string }[];
  fetchedAt: string;
}

/** ตรงกับ `AssetHit` ฝั่ง function */
export interface AssetHit {
  /** ค่าที่ต้องเก็บจริง — ticker ของ Yahoo หรือรหัสเหรียญของ CoinGecko */
  id: string;
  symbol: string;
  name: string;
  detail: string | null;
}

/** ตรงกับ `LookupPriceResult` ฝั่ง function */
export interface LookupPriceResult {
  priceKey: string;
  price: string | null;
  currency: string | null;
  reason: string | null;
  fromCache: boolean;
}

const callRefreshPrices = httpsCallable<void, RefreshPricesResult>(functions, 'refreshPrices');
const callLookupPrice = httpsCallable<{ priceKey: string }, LookupPriceResult>(
  functions,
  'lookupPrice',
);
const callSearchAssets = httpsCallable<{ query: string; assetType: string }, { hits: AssetHit[] }>(
  functions,
  'searchAssets',
);

/** คำค้นสั้นกว่านี้ไม่ยิง — ตรงกับ `MIN_QUERY_LENGTH` ฝั่ง function */
const MIN_QUERY_LENGTH = 2;

/**
 * ค้นหาสินทรัพย์จากชื่อ — ใช้ตอนกรอกฟอร์มเพื่อไม่ให้ผู้ใช้ต้องรู้รหัสของแหล่งราคา
 *
 * `query` ที่ส่งเข้ามาต้อง debounce มาแล้วจากฝั่งที่เรียก การยิงทุกตัวอักษรที่พิมพ์
 * จะกินโควตาก้อนเดียวกับที่ใช้ดึงราคาพอร์ต
 *
 * `retry: false` เพราะคำค้นที่ไม่มีผลลัพธ์ ยิงซ้ำก็ได้ผลเดิม
 */
export function useSearchAssets(query: string, assetType: string, enabled: boolean) {
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['searchAssets', assetType, trimmed.toLowerCase()],
    queryFn: async () => {
      const response = await callSearchAssets({ query: trimmed, assetType });
      return response.data.hits;
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * ดูราคาปัจจุบันของ symbol ที่ยังไม่อยู่ในพอร์ต — ใช้ตอนกรอกฟอร์ม
 *
 * `staleTime` ยาวเพราะระหว่างกรอกฟอร์มเดียว ราคาไม่จำเป็นต้องอัปเดตซ้ำ
 * `retry: false` เพราะ symbol ที่ไม่มีอยู่จริงจะล้มซ้ำอย่างไรก็ไม่สำเร็จ
 */
export function useLookupPrice(priceKey: string | null) {
  return useQuery({
    queryKey: ['lookupPrice', priceKey],
    queryFn: async () => {
      const response = await callLookupPrice({ priceKey: priceKey as string });
      return response.data;
    },
    enabled: priceKey !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export const QUOTES_KEY = 'quotes';
export const FX_KEY = 'fx';

export function useQuotes(priceKeys: readonly string[]) {
  return useQuery({
    // ใส่คีย์ที่เรียงแล้วลงใน queryKey เพื่อให้ cache แยกกันตามชุดที่ขอจริง
    queryKey: [QUOTES_KEY, [...priceKeys].sort()],
    queryFn: () => fetchQuotes(priceKeys),
    enabled: priceKeys.length > 0,
  });
}

export function useFxRates() {
  return useQuery({
    queryKey: [FX_KEY],
    queryFn: fetchFxRates,
  });
}

/**
 * สั่งให้ฝั่ง server ไปดึงราคาใหม่ แล้วโหลดผลลัพธ์กลับมา
 *
 * ตัว function เขียนลง `prices/` และ `fx/` โดยตรง ฝั่งเว็บจึงแค่ invalidate
 * ให้ query อ่านของใหม่ ไม่ต้องเอาค่าที่ function คืนมายัดใส่ cache เอง —
 * แหล่งความจริงมีที่เดียวคือ Firestore
 */
export function useRefreshPrices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<RefreshPricesResult> => {
      const response = await callRefreshPrices();
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUOTES_KEY] }),
        queryClient.invalidateQueries({ queryKey: [FX_KEY] }),
      ]);
    },
  });
}
